import React, { useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
    Glyphicon,
    Section,
} from 'reaxl';

import {
    kbbVRSDataDuck,
} from '@/ducks';

import {
    bapActiveInteractionDuck,
    bapConfigurationDuck,
    bapSelectedOptionsDuck,
} from '@/ducks/bap';

import BuildAndPriceWarningsModal from './BuildAndPriceWarningsModal';
import bapTitleHelper from './utilities/bapTitleHelper';

function BuildAndPriceSummaryContainer() {
    const dispatch = useDispatch();
    const { activeVehicleId = '', lastTouched = null } = useSelector(bapActiveInteractionDuck.selectors.getDuckState) || {};
    const selectedOptions = useSelector(bapSelectedOptionsDuck.selectors.getDuckState);
    const trimInfo = useSelector(kbbVRSDataDuck.selectors.getSelectedTrimInfo) || {};

    const {
        allOptions,
        make = '',
        model = '',
        trimName = '',
    } = trimInfo;

    const sections = {
        make: {
            title: 'Make',
        },
        model: {
            title: 'Model',
        },
        trim: {
            title: 'Trim',
        },
        ...bapTitleHelper,
    };

    // check whether the combination of selections is a valid configuration after each preference change
    useEffect(() => {
        // TODO validate packages for NCCO General Availability feature
        if (lastTouched !== 'packageList') {
            const validConfigurationOptions = {
                allTrimOptions: allOptions,
                selectedOptions,
                vehicleId: activeVehicleId,
            };

            dispatch(bapConfigurationDuck.creators.loadConfigurationData(validConfigurationOptions));
        }
        // only fetch on selectionOptions change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, selectedOptions]);

    const getSelectedPackages = useCallback(() => {
        const { packageList = {} } = selectedOptions;
        const selectedPackages = [];

        if (packageList) {
            Object.keys(packageList).forEach((pkg) => {
                selectedPackages.push(packageList[pkg]?.optionName);
            });
        }

        return selectedPackages.join(', ');
    }, [selectedOptions]);

    const getCurrentSelection = useCallback((section) => {
        let currentOptionSelection;

        switch (section) {
            case 'make':
                currentOptionSelection = make;
                break;
            case 'model':
                currentOptionSelection = model;
                break;
            case 'trim':
                currentOptionSelection = trimName;
                break;
            case 'packageList':
                currentOptionSelection = getSelectedPackages();
                break;
            default:
                currentOptionSelection = selectedOptions[section]?.optionName;
                break;
        }
        return currentOptionSelection;
    }, [selectedOptions, getSelectedPackages, make, model, trimName]);

    const renderPreferenceSummary = Object.entries(sections).map(([key, value]) => (
        <div
            key={key}
            className="margin-bottom-5"
        >
            <div className="text-size-200 text-subdued-lighter">
                <span className="text-bold text-uppercase">{value.title}</span>
                <span>{value.title === 'Packages' && ' (Optional)'}</span>
            </div>
            <div>
                {getCurrentSelection(key) || (
                    <>
                        No Preference Selected
                        {!['Packages'].includes(value.title) && (
                            <Glyphicon
                                glyph="warning"
                                className="margin-left-2 text-yellow"
                                aria-hidden="true"
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    ));

    return (
        <Section
            alignment="left"
            className="bg-neutral-secondary padding-horizontal-4 padding-vertical-5"
            data-cmp="cntnr-prf-smmry"
            heading="Summary"
            headingClassName="text-blue-darker margin-bottom-3"
            style={{
                borderBottom: '1px solid #e1e4e8',
                borderTop: '1px solid #e1e4e8',
            }}
            uiContext="section-3"
        >
            <BuildAndPriceWarningsModal />
            {renderPreferenceSummary}
        </Section>
    );
}

export default React.memo(BuildAndPriceSummaryContainer);
